import React, { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Layout, Form, Card, Row, Col, Input, Button, 
    Affix, Space, message, Radio, InputNumber, Modal } from 'antd';
import { PageHeader } from '@ant-design/pro-layout';
import { useCreateCurriculumMutation, usePatchCurriculumMutation,
    useLocalesQuery } from '../../gql/hooks';
import { CURRICULUM_QUERY } from '../../gql/queries';
import { useParam, useCreateEditLocales  } from '../../hooks';
import { useDeleteCurriculumMutation,
    useSyncObjectRelationshipsMutation } from '../../gql/hooks/mutations';
import { DEFAULT_LOCALE_CODE } from '../../const';
import { CurriculumUtil } from '../../gql/utils';
import { v4 as uuidv4 } from 'uuid';
import { buildPageHeaderBreadcrumb } from '../../updated/utils'; 
import { Routes } from '../../routes';
import { Section } from '../../components/section/Section';
import { LanguageSelector, AssetUpload, TagSelector } 
    from '../../components/formItems';
import RichTextEditor from '../../components/controls/RichTextEditor/RichTextEditor';
import ApolloError from '../../components/ApolloError';
import DeleteButton from '../../components/DeleteButton';
import { useAuthState } from '../../auth/useAuthState';
import SelectOrTransfer from '../../components/selectOrTransfer/SelectOrTransfer';
import { NotFoundErrorView } from '../NotFoundErrorView';
import TinyMCE from '../../components/TinyMCE/TinyMCE';
import { useFeatureFlag, FeatureFlag } from '../../featureFlags';
import { flags } from '../../featureFlags/flags';
import { Loadable } from '../../components/loadable/Loadable';
import { useSegmentation } from '../../hooks/useSegmentation';
import { ErrorPopup } from '../../components/errorPopup/ErrorPopup';
import usePrerequisites from '../../components/formItems/PrerequisiteSelector/usePrerequisites';
import { useMinimalCourseQuery } from '../../gql/hooks/queries/useMinimalCoursesQuery';
import SearchSelector from '../../updated/components/SearchSelector/SearchSelector';
import './styles.less';

const ASSET_DEFAULTS = {
    thumbnailImage: {
        assetType: 'thumbnailImage',
        saveKey: 'thumbnailImageId',
        label: 'Thumbnail Image',
        accept: '.jpg,.png',      
        type: 'image'
    },
    heroImage: {
        assetType: 'heroImage',
        saveKey: 'heroImageId',
        label: 'Hero Image',
        accept: '.jpg,.png',      
        type: 'image'
    },
    bannerImage: {
        assetType: 'bannerImage',
        saveKey: 'bannerImageId',
        label: 'Banner Image',
        accept: '.jpg,.png',
        type: 'image'
    }
};

const CreateEditCurriculumView = ( props ) => {
    const { hasPermission } = useAuthState();
    const canDelete = hasPermission( 'delete-curriculum' );
    const canEdit = hasPermission( 'edit-learning-object' ); 
    const [ isSaving, setIsSaving ] = useState( false );
    const TinyMCERef = useRef( null );
    const showTinyMCE = useFeatureFlag( { name: flags.USE_TINY_MCE } );
    const [ isPrerequiset, setIsPrerequiset ] = useState( false );
    const [ syncRelationships, { loading: isSyncLoading, error: syncRelationshipsError } ] = useSyncObjectRelationshipsMutation();
    const [ failedCourseValidation, setFailedCourseValidation ] = useState( false );

    let initialFormValues = {
        name: '',
        description: '', 
        isPublic: false,
        isSequential: false,
        tags: [], 
        tagIds: [],
        courseIds: [], 
        locale: DEFAULT_LOCALE_CODE,
        categoryIds: [],
        resourceIds: [],
        isFeatured: false,
        weight: 0
    };

    // formController stores form values, validates, enforces rules etc.
    const [ formController ] = Form.useForm(); 

    const { loading: segmentationLoading, addInitialValues, initializeSegmentation, saveSegmentation, renderSegmentation } = useSegmentation( formController );
    initialFormValues = addInitialValues( initialFormValues );
    
    const { renderPrerequisites, setSelectedPrerequisites, savePrerequisites } = 
        usePrerequisites( formController, 'curricula' );

    // Editing (true) or creating (false) an curriculum 
    const [ isEditing, setIsEditing ] = useState( false );
    
    const [ originalName, setOriginalName ] = useState( '' );
    
    // Locales handler hook
    // eslint-disable-next-line no-unused-vars
    const [ currentLocaleCode, setLocales, descriptionRawHTML, handleChangeLocale, setCurrentLocaleCode,  validateEnglishExists ] = useCreateEditLocales( formController );

    const { rawData: resourceRawData, loading: resourcesLoading, error: resourcesError } = useMinimalCourseQuery( {
        types: [ 'resources' ],
        query: '',
        page: 1,
        limit: 10000,
        sort: 'name',
        order: 'asc',
        localeCode: 'en_US',
    } );

    const resourceData = useMemo( () => {
        if ( resourcesLoading || !resourceRawData?.search?.edges ) { return []; }
         
        return resourceRawData.search.edges 
            .filter( res => res.node.isPublished )
            .map( res => ( { 
                key: res.node.resourceId, 
                name: res.node.name.find( i => i.locale === DEFAULT_LOCALE_CODE )?.content || 'Unnamed',
                locales: res.node.resourceLocalizations.map( loc => loc.localeCode ).sort( ( a, b ) => a.localeCompare( b ) ) } ) )
            .map( res => ( {
                key: res.key,
                label: res.name,
                display: ( <div className='option-custom-display'>
                    <span>{ res.name }</span>
                    <div>{ res.locales.map( loc => ( <span key={ loc } className='tag'>{ loc }</span> ) ) }</div>
                </div> )
            } ) ); 
    }, [ resourcesLoading, resourceRawData ] );

    /**
     * ID handling 
     */ 
    
    // Check for the curriculumId in the route
    const { value: paramCurriculumId, checked: checkedRouteForId } = useParam( 'curriculumID' );
  
    // Set the curriculumId, either from the route or if that doesn't exist generate a new one  
    const curriculumId = useMemo( () => { 
        if ( checkedRouteForId ) {
            if ( paramCurriculumId ) { 
                setIsEditing( true );
                formController.setFields( [ { name: 'id', value: paramCurriculumId } ] ); 
                return paramCurriculumId;
            } else {
                setIsEditing( false );
                const uuid = uuidv4();
                formController.setFields( [ { name: 'id', value: uuid } ] ); 
                return uuid;
            }
        } else {
            return undefined;
        }
    }, [ paramCurriculumId, checkedRouteForId, formController ] );
    
    /**
     * Delete Curriculum handling
     */

    const [ deleteItem ] = useDeleteCurriculumMutation();

    const handleDelete = useCallback( async () => {
        try {
            await deleteItem( { variables: { curriculumID: curriculumId } } );

            setTimeout( () => {
                message.success( 'Curriculum Deleted' );
                window.history.back();
            }, 4000 );
        } catch ( e ) { 
            console.error( e ); 

            Modal.error( {
                title: 'Error',
                content: 'This curriculum cannot be deleted because it is a prerequisite of another curriculum.',
            } ); 
        }
    }, [ curriculumId, deleteItem ] );

    /**
     * Fetch curriculum handling 
     */ 
    
    const [ fetchCurriculum, { data: fetchCurriculumUtil, loading: fetchCurriculumLoading, 
        error: fetchCurriculumError, called: fetchCurriculumCalled  } ] = useLazyQuery( CURRICULUM_QUERY, {  fetchPolicy: 'network-only' }  );
    
    const [ createCurriculum, { loading: createLoading, called: createCalled, 
        error: createError } ] = useCreateCurriculumMutation();
            
    // Fetches curriculum if isEditing
    useEffect( () => {
        if ( isEditing && curriculumId  && !createCalled ) {
            fetchCurriculum( { variables: { curriculumID: curriculumId } } );
        } 
    }, [ fetchCurriculum, isEditing, curriculumId, createCalled ] );

    // Use callback so useEffect can call async method
    const setLocaleData = useCallback( async ( curriculumData ) => {
        setLocales( await CurriculumUtil.getLocales( curriculumData ) );
    }, [ setLocales ] );

    // After fetch
    useEffect( () => {
        const curriculumData = fetchCurriculumUtil?.curriculum;

        // Initialize curriculum form items if/when curriculum is loaded.
        if ( curriculumData && !segmentationLoading && !isSaving ) {
            
            initializeSegmentation( curriculumData );
            
            formController.setFields( [ 
                { name: 'tags', value: CurriculumUtil.getTags( curriculumData ) },
                { name: 'tagIds', value: CurriculumUtil.getTagIds( curriculumData ) },
                { name: 'isSequential', value: CurriculumUtil.getIsSequential( curriculumData ) },
                { name: 'isPublic', value: CurriculumUtil.getIsPublic( curriculumData ) },
                { name: 'courseIds', value: CurriculumUtil.getCourseIds( curriculumData ) },
                { name: 'thumbnailImage', value: [ CurriculumUtil.getThumbnailImage( curriculumData ) ] },
                { name: 'heroImage', value: [ CurriculumUtil.getHeroImage( curriculumData ) ] },
                { name: 'categoryIds', value: CurriculumUtil.getCategoryIds( curriculumData ) },
                { name: 'resourceIds', value: curriculumData.node.resourceIds },
                { name: 'isFeatured', value: CurriculumUtil.getIsFeatured( curriculumData ) },
                { name: 'weight', value: CurriculumUtil.getWeight( curriculumData ) }
            ] );
            
            setSelectedPrerequisites( curriculumData );
            setIsPrerequiset( curriculumData.node.prerequisiteStatus.isPrerequisiteOf.length > 0 );
            setOriginalName( CurriculumUtil.getName( curriculumData ) );
            setLocaleData( curriculumData );
        }
    }, [ fetchCurriculumUtil, formController, setLocales, segmentationLoading, isSaving ] );
    
    /**
     * Form Submission, patch, create   
     */

    const [ patchCurriculum, { loading: patchLoading, called: patchCalled,  
        error: patchError } ] = usePatchCurriculumMutation();

    const { data: allLocales, loading: localeLoading } = useLocalesQuery( { fetchPolicy: 'network-only' } );
   
    const handleFormSubmission = async ( formValues ) => {
        setIsSaving( true );

        const [ cmxLocales, nonCmxLocales ] = [
            ( allLocales?.locales?.edges ?? [] ).filter( ( { node } ) => node.cmxSupported ).map( ( { node } ) => node ), 
            ( allLocales?.locales?.edges ?? [] ).filter( ( { node } ) => !node.cmxSupported ).map( ( { node } ) => node ) ];

        // This saves the current name and description fields to the locales array
        let formLocales = handleChangeLocale( currentLocaleCode );

        // Format the locale descriptions to remove any line breaks. New text blocks and spacing will
        // be created through the HTML embedded in the strings, so we don't need the line breaks. Also,
        // we want to get rid of spaces that may exist between the HTML tags.
        formLocales = formLocales
            .filter( loc => loc.name && loc.description )
            .map( ( localeData ) => ( {
                ...localeData,
                description: localeData.description.replace( /[\n\r]/g, '' ).replace( />\s+</g, '><' ),
            } ) );

        const getFormImageId = ( values, propertyName ) => ( values?.[ propertyName ]?.[ 0 ]?.id );

        const previousData = fetchCurriculumUtil?.curriculum;
        const input = CurriculumUtil.transformPatchInput( previousData, {
            id: curriculumId,
            isPublic: formValues[ 'isPublic' ],
            isSequential: formValues[ 'isSequential' ],
            thumbnailImageId: ( getFormImageId( formValues, 'thumbnailImage' ) || null ),
            heroImageId: ( getFormImageId( formValues, 'heroImage' ) || null ),
            bannerImageId: ( getFormImageId( formValues, 'bannerImage' ) || null ),
            locales: formLocales.filter( ( { locale } ) => cmxLocales.some( ( { code } ) => code === locale ) ),
            tags: formValues[ 'tags' ],
            courseIds: formValues[ 'courseIds' ],
            metadata: {
                nonCMXLocales: formLocales.filter( ( { locale } ) => nonCmxLocales.some( ( { code } ) => code === locale ) )
            },
            weight: formValues[ 'weight' ],
            isFeatured: formValues[ 'isFeatured' ]
        } );

       
        
        // Save the new curriculum data.
        ( isEditing )
            ? await patchCurriculum( { variables: { input } } )
            : await createCurriculum( { variables: { input } } );

        await Promise.all( [
            // Update the attached resources.
            syncRelationships( { variables: { input: {
                objectType: 'curriculum',
                objectId: curriculumId,
                relatedResourceType: 'resources',
                relatedResourceIds: formValues[ 'resourceIds' ] || [],
            } } } ),
            // Update the attached tagIds
            syncRelationships( { variables: { input: {
                objectType: 'curriculum',
                objectId: curriculumId,
                relatedResourceType: 'tags',
                relatedResourceIds: formValues[ 'tagIds' ] || [],
            } } } ),
            // Update the attached categories.
            formValues[ 'categoryIds' ] ? 
                syncRelationships( { variables: { input: {
                    objectType: 'curriculum',
                    objectId: curriculumId,
                    relatedResourceType: 'categories',
                    relatedResourceIds: formValues[ 'categoryIds' ]
                } } } ) : Promise.resolve(),
            saveSegmentation( formValues, curriculumId, 'curriculum' ),
        ] );

        await savePrerequisites( curriculumId );

        // Handle completion.
        const hasError = ( patchError || createError || syncRelationshipsError );
        if ( !hasError ) {
            setTimeout( () => {
                message.success( 'Curriculum Saved' );
                window.history.back();
                // if this page has no Back, just refetch. This happens when we get to this page from cateogries
                setIsSaving( false );
            }, 7000 );
        } else {
            message.error( 'Error saving curriculum!' );
            setIsSaving( false );
            fetchCurriculum( { variables: { curriculumID: curriculumId } } );
        }
    };

    /**
     * Bools only used to show/hide DOM elements
     */ 

    const isLoading = useMemo( () => {
        return fetchCurriculumLoading || localeLoading || segmentationLoading ||
        isSyncLoading || createLoading || patchLoading || isSaving;
    }, [ fetchCurriculumLoading, localeLoading, segmentationLoading, isSyncLoading,
        createLoading, patchLoading, isSaving ] ); 

    const isInitilized = useMemo( () => {
        return curriculumId !== undefined;
    }, [ curriculumId ] );

    const hasError = useMemo( () => {
        if ( ( fetchCurriculumCalled && fetchCurriculumError ) ||
           ( createCalled && createError ) ||
           ( patchCalled && patchError )  
        ) {
            return true;
        } else {
            return false;
        }
    }, [ fetchCurriculumError, fetchCurriculumCalled, createCalled, createError, 
        patchCalled, patchError ]  );

    return <FeatureFlag name={ flags.CURRICULA } fallback={ <NotFoundErrorView/> }>
        <Layout className="create-edit-curriculum">
            <Layout>
                { isInitilized && 
                <>
                    <PageHeader
                        title= { isEditing ? 'Edit Curriculum' : 'Create Curriculum' }
                        onBack={ () => window.history.back() }
                        breadcrumb={ buildPageHeaderBreadcrumb( [
                            { breadcrumbName: 'Course Studio', path: Routes.Home.buildPath() },
                            { breadcrumbName: 'Learning Content', path: Routes.LearningContentNav.buildPath() },
                            { breadcrumbName: 'Curricula', path: Routes.Curriculums.buildPath() },
                            { breadcrumbName: isEditing ? 'Edit' : 'Create' },
                        ] ) }
                    />  
                    <ErrorPopup errors={ [ fetchCurriculumError, resourcesError ] } />

                    <Loadable loading={ isLoading } fixed={ true } >
                        <Section>
                            { hasError && <ApolloError { ...patchError } { ...createError } /> }

                            <Form  
                                form={ formController }
                                name="curriculum-form"
                                layout="vertical"
                                initialValues={ initialFormValues }
                                onFinish={ handleFormSubmission }
                                scrollToFirstError
                            >
                                <Row gutter={ [ 24, 24 ] }>
                                    <Col md={ 24 }>
                                        <Card title="General Settings">
                                            <Row gutter={ [ 24, 24 ] }>
                                                <Col xs={ 24 } lg={ 18 }>
                                                    <Form.Item 
                                                        label="Language"
                                                        name="locale"
                                                        rules={ [
                                                            {
                                                                message: 'You need to add a title and description for English (United States)',
                                                                validateTrigger: [],
                                                                validator: ( _, ) => {
                                                                    if ( currentLocaleCode === DEFAULT_LOCALE_CODE ) {
                                                                        return Promise.resolve();
                                                                    }
                                            
                                                                    if ( validateEnglishExists() ) {
                                                                        return Promise.resolve();
                                                                    } else {
                                                                        return Promise.reject();
                                                                    }
                                                                }
                                                            }
                                                        ] }
                                                    >
                                                        <LanguageSelector
                                                            mode="select-single"
                                                            onChange={ ( code ) => handleChangeLocale( code ) }
                                                        />
                                                    </Form.Item>
                                                    
                                                    <Form.Item
                                                        name="name"
                                                        label="Curriculum Name"
                                                        rules={ [
                                                            {
                                                                required: true,
                                                                message: 'Curricula are required to have a name.',
                                                            },
                                                            {
                                                                max: 255,
                                                                type: 'string',
                                                                message: 'Curriculum names can only be 250 characters long.',
                                                            }
                                                        ] }
                                                    >
                                                        <Input 
                                                            placeholder="Curriculum Name"
                                                            disabled={ !canEdit }
                                                        />
                                                    </Form.Item>
                                                    
                                                    { /* Description */ }
                                                    { showTinyMCE && 
                                                        <Form.Item
                                                            name="description"
                                                            label="Content" 
                                                            rules={ [
                                                                {
                                                                    message: 'Curricula are required to have a description.',
                                                                    validateTrigger: [],
                                                                    validator: ( _, ) => {
                                                                        if ( TinyMCERef.current.getCharCount() > 0 ) {
                                                                            return Promise.resolve();
                                                                        } else {
                                                                            return Promise.reject();
                                                                        }
                                                                    }
                                                                }
                                                            ] }    
                                                        >
                                                            <TinyMCE ref={ TinyMCERef } />
                                                        </Form.Item> 
                                                    }

                                                    { !showTinyMCE &&
                                                        <Form.Item
                                                            name="description"
                                                            label="Curriculum Description"                                     
                                                            rules={ [
                                                                {
                                                                    required: true,
                                                                    message: 'Please provide a description.',
                                                                },
                                                            ] }
                                                        >
                                                            <RichTextEditor
                                                                readOnly={ !canEdit }
                                                                controls="simple"  
                                                                locale={ currentLocaleCode }
                                                                rawHTML={ descriptionRawHTML }
                                                            />
                                                        </Form.Item>
                                                    }
                                                </Col>
                                                
                                                <Col xs={ 24 } lg={ 6 }>
                                                    <Form.Item
                                                        name="isSequential"
                                                        label="Content Ordering"
                                                        nostyle="true"
                                                        style={ { display: 'none' } }
                                                    >
                                                        <Radio.Group>
                                                            <Radio disabled={ !canEdit } value={ true }>Sequential</Radio>
                                                            <Radio disabled={ !canEdit } value={ false }>Unordered</Radio>
                                                        </Radio.Group>
                                                    </Form.Item>
                                                    
                                                    <Form.Item
                                                        name="isPublic"
                                                        label="Visibility"
                                                        nostyle="true"
                                                        style={ { display: 'none' } }
                                                    >
                                                        <Radio.Group>
                                                            <Radio disabled={ !canEdit } value={ false }>Private</Radio>
                                                            <Radio disabled={ !canEdit } value={ true }>Public</Radio>
                                                        </Radio.Group>
                                                    </Form.Item>

                                                    <Form.Item
                                                        name="isFeatured"
                                                        label="Featured Curriculum" 
                                                        nostyle="true"
                                                        style={ { display: 'none' } }
                                                    >
                                                        <Radio.Group>
                                                            <Radio disabled={ !canEdit } value={ true }>Featured</Radio>
                                                            <Radio disabled={ !canEdit } value={ false }>Not Featured</Radio>
                                                        </Radio.Group>
                                                    </Form.Item>

                                                    <FeatureFlag name={ flags.CURRICULA_WEIGHT_ENABLED }>  
                                                        <Card title="Sort Order">
                                                            <Form.Item
                                                                name="weight" 
                                                            >
                                                                <InputNumber/>
                                                            </Form.Item>
                                                        </Card>                                                        
                                                    </FeatureFlag>
                                                </Col>
                                            </Row>

                                            <Row gutter={ [ 24, 24 ] }>
                                                <Col xs={ 24 } lg={ 24 }>
                                                    <Card title="Media">
                                                        <Row gutter={ [ 24, 24 ] }>
                                                            <Col sm={ 24 } md={ 12 }> 
                                                                <h4>{ ASSET_DEFAULTS.thumbnailImage.label }</h4>
                                                            
                                                                <Form.Item name="thumbnailImage">
                                                                    <AssetUpload
                                                                        accept={ ASSET_DEFAULTS.thumbnailImage.accept }
                                                                        type={ ASSET_DEFAULTS.thumbnailImage.type }
                                                                    />
                                                                </Form.Item>
                                                            </Col>
                                                                
                                                            <Col sm={ 24 } md={ 12 }>
                                                                <h4>{ ASSET_DEFAULTS.heroImage.label }</h4>
                                                                  
                                                                <Form.Item name="heroImage">
                                                                    <AssetUpload
                                                                        accept={ ASSET_DEFAULTS.heroImage.accept }
                                                                        type={ ASSET_DEFAULTS.heroImage.type }
                                                                    />
                                                                </Form.Item>
                                                            </Col>
                                                        </Row>
                                                    </Card>
                                                </Col>
                                            </Row>

                                            <Row gutter={ [ 24, 24 ] }>
                                                <Col md={ 24 }>
                                                    <Card title="Courses in Curriculum">
                                                        <Form.Item
                                                            name="courseIds" 
                                                            valuePropName="initialItems"
                                                            validateStatus="none"
                                                            rules={ [
                                                                {
                                                                    required: true,
                                                                    validateTrigger: [],
                                                                    message: 'Please select at least one course',
                                                                    validator: ( _, value ) => {
                                                                        if ( value.length > 0 ) {
                                                                            setFailedCourseValidation( false );
                                                                            return Promise.resolve();
                                                                        } else {
                                                                            setFailedCourseValidation( true );
                                                                            return Promise.reject();
                                                                        }
                                                                    }
                                                                },
                                                            ] }
                                                        >
                                                            <SearchSelector 
                                                                type="courses" 
                                                                disabled={  !canEdit }
                                                                reorder={ true }
                                                                showType={ false }
                                                                failedValidation={ failedCourseValidation }
                                                            />
                                                        </Form.Item>
                                                    </Card>
                                                </Col>
                                            </Row>
                                            
                                            <Row gutter={ [ 24, 24 ] }>
                                                <Col md={ 24 }>
                                                    { !isLoading && renderPrerequisites( curriculumId ? [ curriculumId ] : [] ) }
                                                </Col>
                                            </Row>
                                            
                                            <FeatureFlag name={ flags.RESOURCES }>                                            
                                                <Row gutter={ [ 24, 24 ] }>
                                                    <Col xs={ 24 } lg={ 24 }>
                                                        <Card title='Resources'>
                                                            <Form.Item
                                                                label='Resources'
                                                                name='resourceIds'>
                    
                                                                <SelectOrTransfer
                                                                    mode="select"
                                                                    dataSource={ resourceData }
                                                                />

                                                            </Form.Item>
                                                        </Card>
                                                    </Col>
                                                </Row>
                                            </FeatureFlag>

                                            <Row gutter={ [ 24, 24 ] }>
                                                <Col md={ 24 }>
                                                    <Card title="Tags">
                                                        <Form.Item 
                                                            name="tagIds" 
                                                            valuePropName="targetKeys"
                                                        >
                                                            <TagSelector  
                                                                disabled={ !canEdit }
                                                            />
                                                        </Form.Item>
                                                    </Card>
                                                </Col>
                                            </Row>

                                            { renderSegmentation( fetchCurriculumLoading ) }

                                            <FeatureFlag name={ flags.CATEGORIES } >
                                                <Row gutter={ [ 24, 24 ] }>
                                                    <Col md={ 24 }>
                                                        <Card title="Categories">
                                                            <Form.Item 
                                                                name="categoryIds" 
                                                                valuePropName="initialItems"
                                                            >
                                                                <SearchSelector
                                                                    type="categories"  
                                                                    showType={ true }
                                                                />
                                                            </Form.Item>
                                                        </Card>
                                                    </Col>
                                                </Row>
                                            </FeatureFlag>
                                        </Card>
                                    </Col>
                                </Row>   
                            </Form>        
                        </Section>
                    </Loadable>
                </>
                }  
            </Layout> 
            
            <Layout.Sider className="side-action-bar" width={ 100 } style={ { background: 'transparent' } }>
                <Affix offsetTop={ 98 } style={ { marginTop: '98px' } }>
                    <Space direction="vertical" size={ 16 } style={ { width: '100%' } }>
                        { canEdit && 
                            <Button
                                disabled={ isLoading }
                                type="primary"
                                htmlType="submit"
                                style={ { width: '100%' } }
                                onClick={ () => {
                                    formController.submit();
                                } }
                            >
                                Save
                            </Button>
                        }
                        <Button
                            disabled={ isLoading }
                            style={ { width: '100%' } }
                            onClick={ () => window.history.back() }
                        >
                            Cancel
                        </Button>
                        { ( isEditing && canDelete ) && 
                        <DeleteButton
                            disabled={ isLoading || isPrerequiset }
                            itemType="Curriculum"
                            onDelete={ handleDelete }
                            itemName={ originalName }
                            danger
                        >
                            Delete
                        </DeleteButton>
                        }
                    </Space>
                </Affix>
            </Layout.Sider>
        </Layout>
    </FeatureFlag>;
};

export default CreateEditCurriculumView;
          